import { apiPricing, pricingCopyValues } from './pricing';
import { asset } from '../lib/assets';
import i18n from '../i18n';
import { localeFromPath, localizePath, stripLocale, type Locale } from '../lib/locale';

export { localizePath, stripLocale, localeFromPath };

export const SITE_ORIGIN = 'https://oredact.com';
export const SHARE_IMAGE = `${SITE_ORIGIN}${asset('/images/share.jpg')}`;

type PageCopy = { title: string; description: string };
type Page = { path: string; zh: PageCopy; en: PageCopy };

export const pages: Page[] = [
  { path: '/',
    zh: { title: 'ORedact 欧睿达｜打码鸭与图片视频打码 API', description: 'ORedact 提供图片与视频隐私处理，企业 API 邀请接入，按图片张数和视频时长计费。自助处理可使用打码鸭，系统接入先验证样本与业务需求。' },
    en: { title: 'ORedact | Damaya and the Image and Video Redaction API', description: 'Explore image and video redaction with the ORedact API, available by invitation, priced per image and video minute. Use Damaya for self-service or apply for integration assessment.' } },
  { path: '/text-redaction',
    zh: { title: '文字脱敏与敏感信息识别｜ORedact', description: '了解 ORedact 文字隐私处理：识别文本里的姓名、电话、地址、证件号等敏感信息，支持中英文混合，查看脱敏方式、输出格式与客服记录、问卷等处理示例，实体范围按样本确认。' },
    en: { title: 'Text Redaction and Sensitive Information Detection | ORedact', description: 'Explore ORedact text privacy processing: names, phone numbers, addresses and ID numbers detected in mixed Chinese and English text, with redaction methods, output formats and examples from support records and surveys.' } },
  { path: '/file-redaction',
    zh: { title: '文件脱敏需求与格式评估｜ORedact', description: '了解 ORedact 文件脱敏的样本评估流程：先用真实样本确认文件格式、识别对象与输出方式，再约定复核范围；文档需求单独评估，不属于图片与视频标准 API 的计费范围。' },
    en: { title: 'Document Redaction Scope Assessment | ORedact', description: 'Assess document formats, sensitive information and output requirements through sample validation. Document requests are separate from standard image and video API pricing.' } },
  { path: '/image-redaction',
    zh: { title: '图片隐私打码与人脸车牌遮挡｜ORedact', description: '了解 ORedact 图片隐私处理：自动识别人脸、车牌与图片里的电话、地址等敏感文字，用模糊、马赛克或实色遮挡，查看同一张图的处理效果对比与企业 API 的接入方式。' },
    en: { title: 'Image Privacy, Face and License Plate Redaction | ORedact', description: 'Explore ORedact image privacy processing: faces, license plates and sensitive text such as phone numbers and addresses detected automatically and masked with blur, mosaic or solid fill, with side-by-side examples.' } },
  { path: '/video-redaction',
    zh: { title: '视频隐私打码与动态跟踪｜ORedact', description: '了解 ORedact 视频隐私处理：逐帧识别并持续跟踪人脸、车牌与敏感区域，成片按源视频规格输出并保留音轨，支持 4K 与 HEVC，查看真实成片示例、复核方式与接入流程。' },
    en: { title: 'Video Privacy Redaction and Tracking | ORedact', description: 'Explore ORedact video privacy processing: faces, plates and sensitive regions detected and tracked frame by frame, output matching the source specification with audio kept, 4K and HEVC supported, with a real sample clip.' } },
  { path: '/examples',
    zh: { title: '隐私脱敏效果与应用示例｜ORedact', description: '查看 ORedact 图片与视频隐私处理示例：人群、街景、车流的原图与打码结果并排对比，20 秒手机视频的真实成片，以及保留指定人物、编辑器复核的用法；文字和文件脱敏按样本评估。' },
    en: { title: 'Privacy Redaction Examples and Comparisons | ORedact', description: 'View ORedact image and video privacy examples: crowd, street and traffic scenes compared before and after redaction, a real 20-second phone clip, keeping selected people and reviewing objects in the editor.' } },
  { path: '/pricing',
    zh: { title: '企业 API 定价｜图片与视频按量计费｜ORedact', description: `查看 ORedact 企业 API 参考价：图片 ${apiPricing.image.toFixed(2)} 元/张、标准视频 ${apiPricing.videoMinute.toFixed(2)} 元/分钟，${apiPricing.startingBalance} 元起预充值。了解试用额度、适用规格、计费规则和申请流程。` },
    en: { title: 'Enterprise API Pricing | ORedact', description: `Explore ORedact API reference pricing: CNY ${apiPricing.image.toFixed(2)}/image, CNY ${apiPricing.videoMinute.toFixed(2)}/standard video minute and prepayment from CNY ${apiPricing.startingBalance}. Review scope, trial allowances and application steps.` } },
  { path: '/damaya',
    zh: { title: '打码鸭官网入口｜在线 AI 图片视频打码｜ORedact', description: '打码鸭是郑州欧睿达开发的在线 AI 打码工具：上传图片或视频，自动识别人脸、车牌和敏感文字并打码，可保留指定人物，手机电脑浏览器直接用。官网 damaya.cn。' },
    en: { title: 'Damaya | Online AI Image and Video Redaction Tool | ORedact', description: 'Damaya is the ORedact self-service tool: upload an image or video, let it find faces, plates and sensitive text, keep selected people and export from any browser. Runs at damaya.cn.' } },
  { path: '/about',
    zh: { title: '关于欧睿达｜公司介绍与联系方式｜ORedact', description: '了解郑州欧睿达信息科技有限公司：只做图片和视频的隐私打码，自研处理流程和识别模型，线上产品打码鸭与企业 API，以及数据保留承诺、备案信息和联系方式。' },
    en: { title: 'About ORedact | Zhengzhou ORedact Information Technology', description: 'Learn about Zhengzhou ORedact Information Technology: image and video privacy redaction only, an in-house pipeline and detection models, the Damaya web app and the enterprise API, plus data retention commitments and contact details.' } },
  { path: '/privacy-policy',
    zh: { title: '隐私政策｜信息收集、保存与删除｜ORedact', description: '阅读 ORedact 隐私政策：说明收集哪些信息、如何使用与保护，上传内容不用于模型训练，企业 API 数据保留期按协议约定，打码鸭按其隐私政策执行，以及用户权利与联系方式。' },
    en: { title: 'Privacy Policy | Data Collection, Retention and Deletion | ORedact', description: 'Read the ORedact privacy policy: what is collected and how it is used and protected, uploads never used for model training, enterprise API retention agreed by contract, Damaya governed by its own policy, user rights and contacts.' } },
  { path: '/terms-of-service',
    zh: { title: '用户服务协议｜服务范围与使用规则｜ORedact', description: '阅读 ORedact 用户服务协议：图片与视频隐私打码服务的范围、账户规则、使用限制、费用与支付、知识产权、责任限制与联系方式，使用服务前请完整阅读。' },
    en: { title: 'Terms of Service | Scope, Accounts and Usage Rules | ORedact', description: 'Read the ORedact user service agreement: scope of the image and video redaction service, account rules, usage limits, fees and payment, intellectual property, liability limits and contact details.' } },
];

export function normalizePath(pathname: string): string {
  const path = pathname.split(/[?#]/, 1)[0];
  return path.replace(/\/+$/, '') || '/';
}

export function pageUrls(): { path: string; locale: Locale; bare: string }[] {
  return pages.flatMap(page => (['zh', 'en'] as Locale[]).map(locale => ({ path: localizePath(page.path, locale), locale, bare: page.path })));
}

function faqSchema(id: string, locale: Locale, items: [string, string][]) {
  return {
    '@type': 'FAQPage', '@id': id, inLanguage: locale === 'en' ? 'en' : 'zh-CN',
    mainEntity: items.map(([question, answer]) => ({
      '@type': 'Question', name: question, acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };
}

export function getSeo(pathname: string, language?: string) {
  const requested = normalizePath(pathname);
  // The URL decides the language; the optional argument only matters for unprefixed paths that
  // are not pages (the 404 shell), so an English visitor keeps English copy there.
  const locale: Locale = localeFromPath(requested) === 'en' ? 'en'
    : language?.startsWith('en') && !pages.some(p => p.path === requested) ? 'en' : 'zh';
  const english = locale === 'en';
  const path = stripLocale(requested);
  const page = pages.find(item => item.path === path);
  const copy = page?.[english ? 'en' : 'zh'] ?? {
    title: english ? 'Page Not Found | ORedact' : '页面不存在｜ORedact',
    description: english ? 'This page does not exist. Return to the ORedact homepage.' : '该页面不存在，请返回 ORedact 首页继续浏览。',
  };
  const canonical = page ? `${SITE_ORIGIN}${localizePath(path, locale)}` : undefined;
  const alternates = page ? [
    { hreflang: 'zh-CN', href: `${SITE_ORIGIN}${localizePath(path, 'zh')}` },
    { hreflang: 'en', href: `${SITE_ORIGIN}${localizePath(path, 'en')}` },
    { hreflang: 'x-default', href: `${SITE_ORIGIN}${localizePath(path, 'zh')}` },
  ] : [];
  const home = `${SITE_ORIGIN}${localizePath('/', locale)}`;
  const t = i18n.getFixedT(locale);
  const organization = {
    '@type': 'Organization', '@id': `${SITE_ORIGIN}/#organization`,
    name: '郑州欧睿达信息科技有限公司', alternateName: ['ORedact', 'Zhengzhou ORedact Information Technology Co., Ltd.'],
    url: `${SITE_ORIGIN}/`, logo: `${SITE_ORIGIN}/logo.svg`,
    sameAs: ['https://damaya.cn'],
    brand: [{ '@type': 'Brand', name: '打码鸭', alternateName: 'Damaya', url: 'https://damaya.cn' }],
  };
  const graph: Record<string, unknown>[] = page ? [organization, {
    '@type': 'WebSite', '@id': `${SITE_ORIGIN}/#website`, name: 'ORedact',
    url: `${SITE_ORIGIN}/`, publisher: { '@id': organization['@id'] },
  }, {
    '@type': path === '/about' ? 'AboutPage' : 'WebPage', '@id': `${canonical}#webpage`,
    url: canonical, name: copy.title, description: copy.description,
    inLanguage: english ? 'en' : 'zh-CN', isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
    ...(path !== '/' ? { breadcrumb: { '@id': `${canonical}#breadcrumb` } } : {}),
  }] : [];
  if (page && path !== '/') graph.push({
    '@type': 'BreadcrumbList', '@id': `${canonical}#breadcrumb`,
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: english ? 'Home' : '首页', item: home },
      { '@type': 'ListItem', position: 2, name: copy.title.split(/[｜|]/)[0].trim(), item: canonical },
    ],
  });
  // The API is available by invitation only. Reference prices are not purchasable Offers.
  if (path === '/pricing') {
    graph.push({
      '@type': 'SoftwareApplication', '@id': `${SITE_ORIGIN}/#application`,
      name: 'ORedact', applicationCategory: 'SecurityApplication', operatingSystem: 'Web',
      url: canonical, description: copy.description,
    });
    const faq = (i18n.getResourceBundle(locale, 'translation') as { pricing: { faq: Record<string, unknown> } }).pricing.faq;
    graph.push(faqSchema(`${canonical}#faq`, locale, Object.keys(faq).map(key => [
      t(`pricing.faq.${key}.question`, pricingCopyValues), t(`pricing.faq.${key}.answer`, pricingCopyValues)])));
  }
  // Damaya itself is the product entity; it is free to try, so no Offer is published here either.
  if (path === '/damaya') {
    graph.push({
      '@type': 'SoftwareApplication', '@id': 'https://damaya.cn/#application',
      name: '打码鸭', alternateName: 'Damaya', applicationCategory: 'MultimediaApplication', operatingSystem: 'Web',
      url: 'https://damaya.cn', description: copy.description, publisher: { '@id': organization['@id'] },
    });
    graph.push(faqSchema(`${canonical}#faq`, locale, (['q1', 'q2', 'q3', 'q4'] as const).map(key => [t(`damayaPage.${key}`), t(`damayaPage.${key}A`)])));
  }
  return {
    ...copy, canonical, alternates, locale, lang: english ? 'en' : 'zh-CN',
    ogLocale: english ? 'en_US' : 'zh_CN', ogLocaleAlternate: english ? 'zh_CN' : 'en_US',
    robots: page ? 'index, follow' : 'noindex, follow',
    schema: graph.length ? { '@context': 'https://schema.org', '@graph': graph } : undefined,
  };
}

export function seoMeta(seo: ReturnType<typeof getSeo>) {
  return [
    ['name', 'description', seo.description], ['name', 'robots', seo.robots],
    ['property', 'og:type', 'website'], ['property', 'og:site_name', 'ORedact'],
    ['property', 'og:locale', seo.ogLocale], ['property', 'og:locale:alternate', seo.ogLocaleAlternate], ['property', 'og:title', seo.title],
    ['property', 'og:description', seo.description], ['property', 'og:url', seo.canonical],
    ['property', 'og:image', SHARE_IMAGE], ['name', 'twitter:card', 'summary_large_image'],
    ['name', 'twitter:title', seo.title], ['name', 'twitter:description', seo.description],
    ['name', 'twitter:url', seo.canonical], ['name', 'twitter:image', SHARE_IMAGE],
  ] as const;
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]!);
}

export function serializeSchema(schema: unknown): string {
  return JSON.stringify(schema).replace(/</g, '\\u003c');
}

// Verification tokens and the analytics id arrive through the build environment (vite define).
const seoEnv = typeof __SEO_ENV__ === 'undefined' ? { baiduVerification: '', bingVerification: '', googleVerification: '', baiduTongjiId: '' } : __SEO_ENV__;
export function verificationMeta(): [string, string][] {
  return ([
    ['baidu-site-verification', seoEnv.baiduVerification],
    ['msvalidate.01', seoEnv.bingVerification],
    ['google-site-verification', seoEnv.googleVerification],
  ] as [string, string][]).filter(([, value]) => value);
}

export function renderHead(path: string) {
  const seo = getSeo(path);
  return [
    `<title>${escapeHtml(seo.title)}</title>`,
    ...seoMeta(seo).filter(([, , value]) => value !== undefined)
      .map(([attribute, key, value]) => `<meta ${attribute}="${key}" content="${escapeHtml(value!)}" />`),
    ...verificationMeta().map(([key, value]) => `<meta name="${key}" content="${escapeHtml(value)}" />`),
    seo.canonical ? `<link rel="canonical" href="${seo.canonical}" />` : '',
    ...seo.alternates.map(alt => `<link rel="alternate" hreflang="${alt.hreflang}" href="${alt.href}" />`),
    seo.schema ? `<script id="page-schema" type="application/ld+json">${serializeSchema(seo.schema)}</script>` : '',
    seoEnv.baiduTongjiId ? `<script>var _hmt = _hmt || [];(function(){var hm=document.createElement("script");hm.src="https://hm.baidu.com/hm.js?${escapeHtml(seoEnv.baiduTongjiId)}";var s=document.getElementsByTagName("script")[0];s.parentNode.insertBefore(hm,s);})();</script>` : '',
  ].filter(Boolean).join('\n');
}
